import ChatContextButton from './ChatContextButton';
import { useChatContext } from '../../state/ChatContextProvider';
import { Stack, IDropdownOption } from '@fluentui/react';
import { useTranslation } from '../../state/LanguageProvider';
import ChatContextDropdown from './ChatContextDropdown'; 
import styles from './ChatContextSelection.module.css'; 
import { updateObjectKeyInLocalStorage } from '../../utils/localStorageUtils';

type ContextType = Record<string, string>; // Allows dynamic key-value storage

const ChatContextSelection = () => {
    const { chatContextKey, setChatContextKey, availableChatContexts, chatRegionKey } = useChatContext();
    const { getTranslation } = useTranslation();
    const botName = getTranslation('bot_name');

    // Extract contexts, mappings, and regions from availableChatContexts
    const contextsObj: ContextType = availableChatContexts.find((item) => item.contexts)?.contexts || {};
    
    // Ensure mappingsObj is explicitly typed as ContextMappingsType
    const mappingsObj: any = availableChatContexts.find((item) => item.contextMappings)?.contextMappings || {};
    
    const regionObj = availableChatContexts.find((item) => item.region)?.region || null;

    // Function to compute context based on chatRegionKey
    const getFilteredContexts = (): ContextType => {
        // If regionObj doesn't exist, return all contexts without filtering
        if (!regionObj) return contextsObj;

        // Find the region key matching the selected region
        const regionKey = Object.keys(regionObj).find((key) => key === chatRegionKey);

        // If regionKey is not found, return all contexts
        if (!regionKey) return contextsObj;

        // Get mapped context keys for the region
        const mappedKeys = mappingsObj[regionKey] || [];

        if (mappedKeys.length === 0) return contextsObj;

        // Filter contexts based on mapping
        return Object.fromEntries(Object.entries(contextsObj).filter(([key]) => mappedKeys.includes(key)));
    };

    // Get the filtered contexts based on region selection
    const filteredContexts = getFilteredContexts();
    const contextEntries = Object.entries(filteredContexts);

    const setChatContext = (contextKey: string) => {
        updateObjectKeyInLocalStorage(botName, 'context', contextKey);
        setChatContextKey(contextKey);
    };

    function renderContextSelection() {
        // If more than 2 context, then use dropdown
        if (contextEntries.length > 2) {
            const handleContextDropdownChange = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
                if (option?.key) {
                    updateObjectKeyInLocalStorage(botName, 'context', option.key as string);
                    setChatContextKey(option.key as string);
                }
            };
            return (
                <ChatContextDropdown
                    placeholder={chatContextKey}
                    selectedKey={chatContextKey}
                    options={contextEntries.map(([key, text]) => ({ key, text }))}
                    onChange={handleContextDropdownChange}
                />
            );
        }
        return (
            <Stack horizontal tokens={{ childrenGap: 20 }}>
                {contextEntries.map(([key, text]) => (
                    <ChatContextButton
                        key={key}
                        label={text}
                        isActive={chatContextKey === key}
                        onClick={() => setChatContext(localStorage.getItem('context') || key)}
                    />
                ))}
            </Stack>
        );
    }

    return (
        <div>
            <div className={styles.helpMeWithLabel}>
                {getTranslation('help_me_with')}
            </div>
            {renderContextSelection()}
        </div>
    );
};

export default ChatContextSelection;
